/** Legacy DepreciatingAssetTransaction maths (CONVENTIONS §5.10, divergence D2b). */
import { dec, div, fromDouble, fromInt, max, mul, sub, ZERO, type Decimal } from '../core/decimal.ts';
import type { DepreciatingAsset, DepreciationRates } from '../types/domain.ts';
import { PRESET_RATES } from './depreciation.ts';

/** Legacy rates: custom rates apply only with the `custom` preset (unlike unified assets, D3). */
export const legacyDepreciationRates = (asset: DepreciatingAsset): DepreciationRates =>
  asset.depreciationPreset === 'custom' && asset.customDepreciation !== null
    ? asset.customDepreciation
    : PRESET_RATES[asset.depreciationPreset];

const keepAfter = (value: Decimal, ratePercent: Decimal): Decimal => mul(value, sub(dec(1), div(ratePercent, dec(100))));

/**
 * Fractional-year depreciation: whole years at the year-1, year-2 and year-3+ rates, then the
 * Double fraction of a year at the current year's rate, straight-line; floored at 0.
 */
export function legacyDepreciatedValue(price: Decimal, rates: DepreciationRates, monthsOwned: number): Decimal {
  const years = Math.max(0, monthsOwned) / 12;
  const wholeYears = Math.trunc(years);
  const afterWholeYears = Array.from({ length: wholeYears }, (_, year) =>
    year === 0 ? rates.year1 : year === 1 ? rates.year2 : rates.year3Plus,
  ).reduce(keepAfter, price);

  const fraction = years - wholeYears;
  if (fraction <= 0) return max(ZERO, afterWholeYears);
  const rate = years < 1 ? rates.year1 : years < 2 ? rates.year2 : rates.year3Plus;
  const kept = sub(dec(1), mul(div(rate, dec(100)), fromDouble(fraction)));
  return max(ZERO, mul(afterWholeYears, kept));
}

/** DepreciatingAssetTransaction.depreciatedValue(monthsOwned:). */
export const depreciatedValueAfter = (asset: DepreciatingAsset, monthsOwned: number): Decimal =>
  legacyDepreciatedValue(asset.purchasePrice, legacyDepreciationRates(asset), monthsOwned);

/** Embedded loan finance still owed: payment × max(0, term − min(monthsOwned, term)); 0 unless a loan. */
export function remainingEmbeddedFinance(asset: DepreciatingAsset, monthsOwned: number): Decimal {
  const finance = asset.finance;
  if (finance === null || finance.type !== 'loan' || finance.monthlyPayment === null || finance.termMonths === null) {
    return ZERO;
  }
  const remainingMonths = Math.max(0, finance.termMonths - Math.min(monthsOwned, finance.termMonths));
  return mul(finance.monthlyPayment, fromInt(remainingMonths));
}

/** totalMonthlyCosts: running costs only (finance is a separate debt). */
export const depreciatingAssetMonthlyCosts = (asset: DepreciatingAsset): Decimal => asset.monthlyRunningCosts ?? ZERO;
